import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { livekitService, ConnectionStateCallback, ParticipantCallback } from "@/services/livekitService";
import {
    updateConnectionStatus,
    endCallCleanup
} from "@features/call/store/callSlice";
import { ConnectionState, DisconnectReason, RemoteParticipant, RoomEvent, Track } from "livekit-client";
import { selectActiveCall } from "@features/call/store/callSlice";
import { AppDispatch, RootState } from "@/store/store";
import { emitCallConnected } from "@/socket/emitters";
import { onCallConnectedConfirmed, onCallConnectedError } from "@/socket/listeners";

/**
 * CallLiveKitManager - Manages LiveKit room connection based on Redux activeCall state
 * 
 * Flow:
 * 1. When activeCall is set in Redux (with token + ws_url), connects to LiveKit
 * 2. Enables camera and microphone after connection
 * 3. **Emits call:connected to backend** to confirm participant joined
 * 4. Syncs connection state back to Redux
 * 5. Disconnects when activeCall is cleared
 */
const CallLiveKitManager: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const activeCall = useSelector(selectActiveCall);
    const userId = useSelector((state: RootState) => state.auth?.user?.id);

    // Listen for call:connected:confirmed and call:connected:error
    useEffect(() => {
        const unsubConfirmed = onCallConnectedConfirmed((payload) => {
            console.log("[CallLiveKitManager] ✅ Backend confirmed connection:", payload);
        });

        const unsubError = onCallConnectedError((payload) => {
            console.error("[CallLiveKitManager] ❌ Backend connection error:", payload.message);
        });

        return () => {
            unsubConfirmed();
            unsubError();
        };
    }, []);

    // Connect/Disconnect logic based on activeCall presence
    useEffect(() => {
        const connectToLiveKit = async () => {
            if (!activeCall || !activeCall.token || !activeCall.ws_url) {
                console.log("[CallLiveKitManager] No active call or missing token/ws_url");
                return;
            }

            const currentState = livekitService.getConnectionState();
            console.log("[CallLiveKitManager] Current connection state:", currentState);

            if (currentState === ConnectionState.Connected) {
                console.log("[CallLiveKitManager] Already connected to room");
                return;
            }

            if (currentState === ConnectionState.Connecting) {
                console.log("[CallLiveKitManager] Already connecting...");
                return;
            }

            try {
                console.log("[CallLiveKitManager] ========================================");
                console.log("[CallLiveKitManager] CONNECTING TO LIVEKIT ROOM");
                console.log("[CallLiveKitManager] Room:", activeCall.room_name);
                console.log("[CallLiveKitManager] WS URL:", activeCall.ws_url);
                console.log("[CallLiveKitManager] Token (first 20 chars):", activeCall.token?.substring(0, 20) + "...");
                console.log("[CallLiveKitManager] ========================================");

                const room = await livekitService.connectToRoom(activeCall.ws_url, activeCall.token);

                console.log("[CallLiveKitManager] ✅ Connected to room:", room.name);
                console.log("[CallLiveKitManager] Local participant:", room.localParticipant.identity);

                // ===================================================
                // CRITICAL: Notify backend that we connected to room
                // This triggers CallConnectionHandler on the backend
                // which updates call status to 'connected' and notifies
                // admin + other participants
                // ===================================================
                const participantId = userId || room.localParticipant.identity;
                console.log("[CallLiveKitManager] 📡 Emitting call:connected to backend...");
                emitCallConnected(activeCall.id, participantId);

                // Enable camera and microphone
                console.log("[CallLiveKitManager] Enabling camera and microphone...");
                try {
                    await room.localParticipant.setCameraEnabled(true);
                    console.log("[CallLiveKitManager] ✅ Camera enabled");
                } catch (cameraErr) {
                    console.warn("[CallLiveKitManager] ⚠️ Failed to enable camera:", cameraErr);
                }

                try {
                    await room.localParticipant.setMicrophoneEnabled(true);
                    console.log("[CallLiveKitManager] ✅ Microphone enabled");
                } catch (micErr) {
                    console.warn("[CallLiveKitManager] ⚠️ Failed to enable microphone:", micErr);
                }

                // Log current tracks
                console.log("[CallLiveKitManager] Local video tracks:", room.localParticipant.videoTrackPublications.size);
                console.log("[CallLiveKitManager] Local audio tracks:", room.localParticipant.audioTrackPublications.size);

                room.localParticipant.videoTrackPublications.forEach((pub, sid) => {
                    console.log("[CallLiveKitManager] Video track:", sid, "enabled:", !pub.isMuted);
                });

                room.localParticipant.audioTrackPublications.forEach((pub, sid) => {
                    console.log("[CallLiveKitManager] Audio track:", sid, "enabled:", !pub.isMuted);
                });

                // Log remote participants
                console.log("[CallLiveKitManager] Remote participants:", room.remoteParticipants.size);
                room.remoteParticipants.forEach((p) => {
                    console.log("[CallLiveKitManager] Remote:", p.identity, "Video:", p.videoTrackPublications.size, "Audio:", p.audioTrackPublications.size);
                });

            } catch (err) {
                console.error("[CallLiveKitManager] ❌ Failed to connect:", err);
                dispatch(endCallCleanup());
            }
        };

        connectToLiveKit();
    }, [activeCall, dispatch]);

    // Cleanup when call ends
    useEffect(() => {
        if (!activeCall && livekitService.getConnectionState() !== ConnectionState.Disconnected) {
            console.log("[CallLiveKitManager] No active call in Redux - disconnecting from LiveKit");
            livekitService.disconnect();
            livekitService.clearCallbacks();
        }
    }, [activeCall]);

    // Setup Listeners for room events
    useEffect(() => {
        const handleConnectionStateChange: ConnectionStateCallback = (state) => {
            console.log("[CallLiveKitManager] Connection State Changed:", state);
            let status: "connected" | "disconnected" | "reconnecting" | "idle" = "idle";
            switch (state) {
                case ConnectionState.Connected: status = "connected"; break;
                case ConnectionState.Reconnecting: status = "reconnecting"; break;
                case ConnectionState.Disconnected: status = "disconnected"; break;
                default: status = "idle";
            }
            dispatch(updateConnectionStatus(status));
        };

        const handleDisconnected = (reason?: DisconnectReason) => {
            console.log("[CallLiveKitManager] Disconnected, reason:", reason);
            dispatch(endCallCleanup());
        };

        const handleParticipantConnected: ParticipantCallback = (p) => {
            console.log("[CallLiveKitManager] 👤 Participant connected:", p.identity);
            console.log("[CallLiveKitManager]    Video tracks:", p.videoTrackPublications.size);
            console.log("[CallLiveKitManager]    Audio tracks:", p.audioTrackPublications.size);
        };

        const handleParticipantDisconnected: ParticipantCallback = (p) => {
            console.log("[CallLiveKitManager] 👤 Participant disconnected:", p.identity);
        };

        livekitService.setCallbacks({
            onConnectionStateChange: handleConnectionStateChange,
            onDisconnected: handleDisconnected,
            onParticipantConnected: handleParticipantConnected,
            onParticipantDisconnected: handleParticipantDisconnected
        });

        // Also listen to track events on the room directly
        const room = livekitService.getRoom();
        if (room) {
            room.on(RoomEvent.TrackSubscribed, (track, publication, participant) => {
                console.log("[CallLiveKitManager] 🎥 Track subscribed:", track.kind, "from", participant.identity);
            });
            room.on(RoomEvent.TrackUnsubscribed, (track, publication, participant) => {
                console.log("[CallLiveKitManager] 🎥 Track unsubscribed:", track.kind, "from", participant.identity);
            });
            room.on(RoomEvent.LocalTrackPublished, (publication, participant) => {
                console.log("[CallLiveKitManager] 📤 Local track published:", publication.kind, publication.trackSid);
            });
            room.on(RoomEvent.LocalTrackUnpublished, (publication, participant) => {
                console.log("[CallLiveKitManager] 📤 Local track unpublished:", publication.kind);
            });
        }

        return () => {
            livekitService.clearCallbacks();
        };
    }, [dispatch]);

    return null;
}

export default CallLiveKitManager;
